import React from 'react'
import { Alert } from 'antd'
import { usePQueryBuilder } from '@pushly/aqe'

export interface ReadOnlyNoticeProps {
    /**
     * True when the record is one this level may show but not write — a segment several domains share,
     * opened through a single domain.
     *
     * Passed in rather than derived, because it is a fact about the record and the route it was opened
     * through, neither of which the builder knows. Deliberately not "the builder is read-only": a
     * consumer can disable editing for reasons of its own, and this notice would then state a reason
     * that is not the reason.
     */
    multiDomainLock?: boolean
}

/**
 * States that the open segment is being shown rather than edited, and why, for exactly as long as
 * that is true.
 *
 * Every reason editing is off needs its own sentence. A reader who cannot act on the reason still has
 * to know their work is safe; a reader who *can* act on it — a shared segment is editable one level
 * up — has to be told where to go. One message covering both would be wrong for one of them.
 *
 * Unrepresentability is read from the builder rather than remembered, so widening the field catalog —
 * the same change that lets the builder edit the segment — withdraws the notice. A remembered verdict
 * would outlive the document it was about and leave a builder showing editable rows under a notice
 * saying they cannot be edited.
 *
 * It also wins over the shared-segment reason when both hold: a segment the builder cannot represent
 * cannot be edited one level up either, so sending the reader there would waste the trip.
 *
 * Not closable: dismissing it would leave a builder that silently refuses every save.
 */
export function ReadOnlyNotice({ multiDomainLock }: ReadOnlyNoticeProps) {
    const { isSupported } = usePQueryBuilder()

    if (!isSupported) {
        return (
            <Alert
                type="warning"
                title="Viewing in Read-Only Mode"
                description="This segment includes one or more conditions that aren't supported by this builder. To protect your work from being overwritten, editing is disabled, but you can still review the segment details below."
            />
        )
    }

    if (multiDomainLock) {
        return (
            <Alert
                type="warning"
                title="Viewing in Read-Only Mode"
                description="This segment applies to more than one domain, so it can only be edited in multi-domain mode. To protect your work from being overwritten, editing is disabled here, but you can still review the segment details below."
            />
        )
    }

    return null
}
